import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";

const LEADERBOARD_TABS = [
  { label: "Top Chemist", type: "pharmacy_points" },
  { label: "Streak", type: "pharmacy_streak" },
  { label: "Top Staff", type: "staff_appointments" },
];

const RankBadge = ({ rank }) => {
  const medals = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32" };
  const bg = medals[rank] || DOCLINK_COLORS.LIGHT_PURPLE;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" fontWeight={800} sx={{ color: rank <= 3 ? "#fff" : DOCLINK_COLORS.DARK_PURPLE, lineHeight: 1 }}>
        {rank}
      </Typography>
    </Box>
  );
};

const LeaderboardList = ({ rows, isStaff, onOpenChemist }) => {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        No data yet for this competition period.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {rows.map((row) => (
        <Box
          key={row.rank}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <RankBadge rank={row.rank} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
              {row.displayName || "—"}
            </Typography>
            {isStaff && row.pharmacyName && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.pharmacyName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2" fontWeight={800} sx={{ color: DOCLINK_COLORS.MID_PURPLE }}>
                {row.score}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                pts
              </Typography>
            </Box>
            {row.chemistUUID && (
              <Tooltip title="Open chemist" placement="left">
                <IconButton
                  size="small"
                  onClick={() => onOpenChemist(row.chemistUUID)}
                  sx={{ color: DOCLINK_COLORS.MID_PURPLE }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DocLinkLeaderboardDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  // Sequence counter — incremented before every fetch, checked after await.
  // Any response whose seq doesn't match the current value is stale and discarded.
  const fetchSeqRef = useRef(0);

  const fetchLeaderboard = useCallback(async (periodId, tabIdx) => {
    if (!periodId) return;
    const seq = ++fetchSeqRef.current;
    setRowsLoading(true);
    setRows([]);
    try {
      const payload = await phappsRequest(API_PATHS.DOCLINK_ADMIN_LEADERBOARD, "POST", {
        competitionPeriodId: periodId,
        leaderboardType: LEADERBOARD_TABS[tabIdx].type,
      });
      if (seq !== fetchSeqRef.current) return; // stale — a newer request is in flight
      setRows(payload?.data ?? []);
    } catch {
      if (seq !== fetchSeqRef.current) return;
      setRows([]);
    } finally {
      if (seq === fetchSeqRef.current) setRowsLoading(false);
    }
  }, []);

  // Fetch competitions and immediately trigger the leaderboard fetch for tab 0.
  // Calling fetchLeaderboard directly here avoids the "same-value state no-op" problem
  // where setSelectedPeriodId(sameId) wouldn't re-trigger a useEffect.
  const fetchCompetitions = useCallback(async (tabIdx) => {
    setCompetitionsLoading(true);
    try {
      const payload = await phappsRequest(API_PATHS.DOCLINK_ADMIN_LEADERBOARD_COMPETITIONS);
      const list = payload?.data ?? [];
      setCompetitions(list);
      if (list.length > 0) {
        const periodId = list[0].periodId;
        setSelectedPeriodId(periodId);
        fetchLeaderboard(periodId, tabIdx);
      }
    } catch {
      setCompetitions([]);
    } finally {
      setCompetitionsLoading(false);
    }
  }, [fetchLeaderboard]);

  // On open: reset to tab 0, clear rows, reload everything.
  useEffect(() => {
    if (open) {
      fetchSeqRef.current = 0;
      setTabIndex(0);
      setRows([]);
      fetchCompetitions(0);
    }
  }, [open, fetchCompetitions]);

  // Tab change: update index and fetch immediately (don't wait for state to propagate).
  const handleTabChange = useCallback((_, v) => {
    setTabIndex(v);
    fetchLeaderboard(selectedPeriodId, v);
  }, [fetchLeaderboard, selectedPeriodId]);

  // Period selector change: update id and fetch immediately.
  const handlePeriodChange = useCallback((e) => {
    const id = Number(e.target.value);
    setSelectedPeriodId(id);
    fetchLeaderboard(id, tabIndex);
  }, [fetchLeaderboard, tabIndex]);

  const selectedComp = competitions.find((c) => c.periodId === selectedPeriodId);
  const isStaff = LEADERBOARD_TABS[tabIndex].type === "staff_appointments";

  const handleOpenChemist = useCallback((chemistUUID) => {
    onClose?.();
    navigate(`/admin-functions/chemist/${encodeURIComponent(chemistUUID)}`, { state: { tab: 4 } });
  }, [navigate, onClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          overflow: "hidden",
          boxShadow: "-8px 0 24px rgba(64, 29, 94, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 520 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "grey.50",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmojiEventsIcon sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
              Leaderboard
            </Typography>
          </Box>
          <IconButton aria-label="Close drawer" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {competitionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={36} sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
            </Box>
          ) : competitions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No active competitions right now.
            </Typography>
          ) : (
            <>
              {/* Competition selector */}
              {competitions.length > 1 ? (
                <Select
                  size="small"
                  value={selectedPeriodId ?? ""}
                  onChange={handlePeriodChange}
                  fullWidth
                  sx={{ bgcolor: "background.paper" }}
                >
                  {competitions.map((c) => (
                    <MenuItem key={c.periodId} value={c.periodId}>
                      {c.competitionName}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: DOCLINK_COLORS.LAVENDER_TINT,
                    border: "1px solid",
                    borderColor: DOCLINK_COLORS.LIGHT_PURPLE,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
                    {selectedComp?.competitionName}
                  </Typography>
                  {selectedComp && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(selectedComp.startsAt).toLocaleDateString()} –{" "}
                      {new Date(selectedComp.endsAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Tabs */}
              <Box sx={{ bgcolor: "background.paper", borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13 },
                    "& .Mui-selected": { color: DOCLINK_COLORS.DARK_PURPLE },
                    "& .MuiTabs-indicator": { bgcolor: DOCLINK_COLORS.DARK_PURPLE },
                  }}
                >
                  {LEADERBOARD_TABS.map((t) => (
                    <Tab key={t.type} label={t.label} />
                  ))}
                </Tabs>

                <Box sx={{ p: 2 }}>
                  {rowsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={28} sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
                    </Box>
                  ) : (
                    <LeaderboardList rows={rows} isStaff={isStaff} onOpenChemist={handleOpenChemist} />
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default DocLinkLeaderboardDrawer;
